import CloseIcon from '@mui/icons-material/Close';
import { Snackbar, IconButton, Card, CardContent, Typography, styled } from '@mui/material';
import React, { useState, useEffect } from 'react';

import { useWorkspaceContext } from '../../context';
import { getFCMTokenAndSubscribe } from '../../firebase/FirebaseMessaging';

export interface FcmNotificationRef {
  showNotification: (notification: INotificationPayload) => void;
}

export interface INotificationPayload {
  title: string;
  body: string;
  link?: string;
}

interface FcmNotificationProps {
  notification: INotificationPayload | null;
}

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#f1f8ff',
  borderLeft: '6px solid rgb(25, 158, 210)',
  boxShadow: theme.shadows[3],
  width: 360,
  position: 'relative',
}));

const FcmNotification: React.FC<FcmNotificationProps> = ({ notification }) => {
  const [open, setOpen] = useState(false);
  const workspace = useWorkspaceContext();

  useEffect(() => {
    if (notification) {
      setOpen(true);
    }
  }, [notification]);

  useEffect(() => {
    if (workspace?.id) {
      const generateToken = async () => {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          // Make sure service worker is registered
          if ('serviceWorker' in navigator) {
            try {
              const registration = await navigator.serviceWorker.register(
                '/firebase-messaging-sw.js'
              );
              console.log('Service Worker registered:', registration.scope);
              console.log(localStorage.getItem('FCMToken'));
              if (!localStorage.getItem('FCMToken')) {
                console.log('Generating the FCM Token');
                await getFCMTokenAndSubscribe();
              }
            } catch (err: any) {
              throw new Error(
                'Failed to register service worker: ' + (err?.message || 'Unknown error')
              );
            }
          }
        }
      };

      generateToken();
    }
  }, [workspace?.id]);

  const closeNotification = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleCardClick = () => {
    if (notification?.link?.trim()) {
      window.open(notification.link, '_blank', 'noopener');
      setOpen(false);
    }
  };

  if (!notification) return null;

  return (
    <Snackbar
      open={open}
      onClose={closeNotification}
      autoHideDuration={8000}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <StyledCard
        onClick={handleCardClick}
        style={{
          cursor: notification.link ? 'pointer' : 'default',
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            closeNotification();
          }}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {notification.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {notification.body}
          </Typography>
        </CardContent>
      </StyledCard>
    </Snackbar>
  );
};

export default FcmNotification;
